import React, { useCallback, useMemo, memo } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase/AutocompleteBase'
import { useAutocompleteInputState } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteInputState'
import { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import { TAutocompleteBaseChangeHandler } from 'transfix-ui/components/AutocompleteBase/types'
import { AutocompleteInputChangeReason } from 'transfix-ui/core'
import { ICarrierUserTypes, IUser, IUserTypeTypes } from 'types/graphqlTypes'
import formatPhone from 'utils/formatPhone'
import { ICarrierUserResultFragment } from './graphql/CarrierUserResultFragment'
import { useSearchCarrierUsersQuery } from './graphql/SearchCarrierUsers'

const DEBOUNCE_AMOUNT = 250

const generateCarrierOption = (user: ICarrierUserResultFragment) => {
  const { cellPhone, email, fullName, id, phone, userType } = user
  const driverText = `${fullName} · ${formatPhone(cellPhone)}`
  const dispatcherText = `${fullName} · ${email} · ${formatPhone(phone)}`
  return {
    id,
    text: userType === IUserTypeTypes.driver ? driverText : dispatcherText,
    user,
    fullName,
  }
}

type TAutocompleteOmittedProps = Omit<
  React.ComponentProps<typeof AutocompleteBase>,
  'options' | 'loading' | 'defaultValue' | 'onChange' | 'onInputChange' | 'value' | 'inputValue'
>

export interface ICarrierUserAutosuggestProps extends ControllerRenderProps {
  carrierId: string
  user?: ICarrierUserResultFragment | null
  defaultInputValue?: string | null
  onInputChange?: (value: string) => void
  onUserChange?: (user: IUser | null) => void
  userTypes?: ICarrierUserTypes[]
  AutocompleteProps?: TAutocompleteOmittedProps
}

const CarrierUserAutosuggest = memo<ICarrierUserAutosuggestProps>(
  ({
    carrierId,
    AutocompleteProps = {},
    onInputChange: onInputChangeProp,
    onChange,
    onUserChange,
    user,
    defaultInputValue,
    userTypes = [ICarrierUserTypes.owner_operator, ICarrierUserTypes.dispatcher],
    value,
  }) => {
    const [inputValue, inputChangeHandler] = useAutocompleteInputState(defaultInputValue || '')

    const { options = [], loading, refetch: fetchUsers } = useAutocompleteQuery(
      useSearchCarrierUsersQuery,
      {
        fetchPolicy: 'network-only',
        variables: {
          carrierId,
          userTypes,
          name: defaultInputValue || '',
        },
      },
      {
        value: user ? generateCarrierOption(user) : {},
        getOptionsConfig: data => {
          const { carrierApplicationUsers: users = [] } = data || {}

          const drivers = users.filter(({ userType }) => userType === IUserTypeTypes.driver)
          const dispatchers = users.filter(({ userType }) => userType === IUserTypeTypes.dispatcher)
          const ownerOperators = users.filter(
            ({ userType }) => userType === IUserTypeTypes.owner_operator
          )
          return [
            {
              options: drivers,
              optionType: 'withAvatar',
              groupIndex: 0,
              groupName: 'Drivers',
              mapOption: generateCarrierOption,
            },
            {
              options: dispatchers,
              optionType: 'withAvatar',
              groupIndex: 0,
              groupName: 'Dispatcher',
              mapOption: generateCarrierOption,
            },
            {
              options: ownerOperators,
              optionType: 'withAvatar',
              groupIndex: 1,
              groupName: 'Owner Operator',
              mapOption: generateCarrierOption,
            },
          ]
        },
      }
    )

    const debouncedChangeHandler = useCallback(
      (searchValue: string) => {
        debounce(() => {
          fetchUsers({ carrierId, userTypes, name: searchValue })
        }, DEBOUNCE_AMOUNT)()
      },
      [fetchUsers, carrierId, userTypes]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = useCallback(
      (_, selectedItem) => {
        onChange(get(selectedItem, 'id', undefined))
        if (onUserChange) {
          onUserChange(get(selectedItem, 'user', null))
        }
      },
      [onChange, onUserChange]
    )

    const inputHandler = useCallback(
      (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
        if (event || (!event && value.length)) {
          inputChangeHandler(event, value)

          if ((reason === 'input' || reason === 'clear') && onInputChangeProp) {
            onInputChangeProp(value)
          }

          if (reason === 'input') {
            debouncedChangeHandler(value)
          }
        }
      },
      [debouncedChangeHandler, inputChangeHandler, onInputChangeProp]
    )

    const computedValue = useMemo(() => {
      return options.find(({ id }) => id === value) || null
    }, [options, value])

    return (
      <AutocompleteBase
        {...AutocompleteProps}
        options={options}
        loading={loading}
        filterOptionsType='fuzzy'
        onChange={changeHandler}
        onInputChange={inputHandler}
        value={computedValue}
        inputValue={inputValue}
      />
    )
  }
)

export default CarrierUserAutosuggest
